import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import React, { useState } from "react";
import { ReactComponent as FilterSvg } from "../../../assets/icons/Icon/DKT/Filter black.svg";

import { css } from "@emotion/react";

import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { SwarovskiRadio } from "../../../components/_shared-components/ui-elements/SwarovskiRadio";
import { UITags } from "../../../components/_shared-components/ui-elements/UITags";
import { AppliedFiltersT, AppliedFilterT } from "./events-overview-page-layout";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { CSS_MQ } from "../../../styles/breakpoints";

export type FilteredLabeledStringValue = { __tag: "string"; label: string; value: string };
export type FilteredLabeledDateValue = { __tag: "date"; label: string; value: Date };

export type FilterBarFilter = {
  country: { topLabel: string; values: Array<FilteredLabeledStringValue> };
  date: { topLabel: string; values: Array<FilteredLabeledDateValue> };
};

type FilteredStringLabelsState = {
  __tag: "string";
  topLabel: string;
  values: Array<FilteredLabeledStringValue>;
};

type FilteredDateLabelsState = {
  __tag: "date";
  topLabel: string;
  values: Array<FilteredLabeledDateValue>;
};

export type OpenedFilterState = undefined | FilteredStringLabelsState | FilteredDateLabelsState;

export const EventsOverviewFilterBar: ReactTFC<{
  filters: FilterBarFilter;
  appliedFilters: AppliedFiltersT;
  setAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFiltersT>>;
  facetedFilter: (filter: AppliedFiltersT) => number;
}> = props => {
  const [openedFilter, setOpenedFilter] = useState<OpenedFilterState>(undefined);

  function onFilterClick(filter: OpenedFilterState) {
    setOpenedFilter(s => {
      if (s && filter && s.__tag == filter.__tag) {
        return undefined;
      } else {
        return filter;
      }
    });
  }

  return (
    <React.Fragment>
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <FilterSvg
          css={css`
            width: 24px;
            height: 24px;

            margin-left: 5px; // align visually
            margin-right: 25px;
          `}
        />

        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <FilterButton
            isSelected={props.filters.date.topLabel === openedFilter?.topLabel}
            onClick={() => onFilterClick({ __tag: "date", ...props.filters.date })}
            label={props.filters.date.topLabel}
          />
          <FilterButton
            isSelected={props.filters.country.topLabel === openedFilter?.topLabel}
            onClick={() => onFilterClick({ __tag: "string", ...props.filters.country })}
            label={props.filters.country.topLabel}
          />
        </div>
      </div>

      {openedFilter && (
        <div
          css={css`
            ${CSS_MQ.atMostSm} {
              max-height: 150px;
              overflow-y: scroll;
            }

            margin-left: 40px;
            margin-top: 15px;
          `}
        >
          <OpenedFilterUI
            openedFilter={openedFilter}
            facetedFilter={props.facetedFilter}
            appliedFilters={props.appliedFilters}
            setAppliedFilters={props.setAppliedFilters}
          />
        </div>
      )}

      <UITags
        elements={[
          ...(props.appliedFilters.dateFilter
            ? [
                {
                  key: props.appliedFilters.dateFilter.filterKey,
                  label: props.appliedFilters.dateFilter.filterLabel,
                  onClick: () =>
                    props.setAppliedFilters({
                      ...props.appliedFilters,
                      dateFilter: undefined,
                    }),
                },
              ]
            : []),
          ...(props.appliedFilters.countryFilter
            ? [
                {
                  key: props.appliedFilters.countryFilter.filterKey,
                  label: props.appliedFilters.countryFilter.filterLabel,
                  onClick: () =>
                    props.setAppliedFilters({
                      ...props.appliedFilters,
                      countryFilter: undefined,
                    }),
                },
              ]
            : []),
        ]}
      />
    </React.Fragment>
  );
};

const OpenedFilterUI: ReactTFC<{
  openedFilter: NonNullable<OpenedFilterState>;
  facetedFilter: (filter: AppliedFiltersT) => number;
  appliedFilters: AppliedFiltersT;
  setAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFiltersT>>;
}> = props => {
  const { openedFilter } = props;

  // Diluting types :(
  const values: Array<FilteredLabeledStringValue | FilteredLabeledDateValue> = openedFilter.values;

  return (
    <ul
      css={css`
        display: inline-flex;
        flex-direction: column;
        flex-wrap: wrap;

        ${CSS_MQ.atLeastMd} {
          max-height: 200px;
        }
        gap: 10px;

        li {
          width: 100%;
          ${CSS_MQ.atLeastMd} {
            width: 300px;
          }
        }
      `}
    >
      {values.map(v => {
        const toggledFilter =
          v.__tag === "date"
            ? {
                ...props.appliedFilters,
                dateFilter: { filterKey: openedFilter.topLabel, filterValue: v.value, filterLabel: v.label },
              }
            : {
                ...props.appliedFilters,
                countryFilter: { filterKey: openedFilter.topLabel, filterValue: v.value, filterLabel: v.label },
              };

        const facetedSearchCount = props.facetedFilter(toggledFilter);

        const isDisabled = facetedSearchCount === 0;

        function getLabelValue(): React.ReactNode {
          let ret = v.label;

          if (!isDisabled) {
            return (
              <>
                {ret} <span>({facetedSearchCount.toString()})</span>
              </>
            );
          } else {
            return <>{ret}</>;
          }
        }

        const vv = typeof v.value === "string" ? v.value : v.value.toString();

        return (
          <li key={vv}>
            <SwarovskiRadio
              type={"radio"}
              inputProps={{
                value: vv,
                checked:
                  v.__tag === "date"
                    ? props.appliedFilters.dateFilter?.filterLabel === v.label
                    : props.appliedFilters.countryFilter?.filterLabel === v.label,
                disabled: isDisabled,
                onChange: () => {
                  props.setAppliedFilters(toggledFilter);
                },
              }}
              labelProps={{}}
            >
              {getLabelValue()}
            </SwarovskiRadio>
          </li>
        );
      })}
    </ul>
  );
};

const FilterButton: ReactTFC<{ isSelected: boolean; onClick: () => void; label: string }> = props => {
  return (
    <button
      className={props.isSelected ? "selected" : undefined}
      onClick={() => props.onClick()}
      css={[
        FONTS.SwarovskiFutura300,
        fontColor.black,
        fontSize.x2,
        css`
          text-transform: uppercase;

          margin-top: 4px; // center visually

          border-bottom: 2px solid transparent;

          &:hover,
          &.selected {
            border-bottom-color: ${CSS_COLORS.Green};
            // color: ${CSS_COLORS.Green};
          }
        `,
      ]}
    >
      {props.label}
    </button>
  );
};
